import React, {useCallback, useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useSelector, useDispatch} from 'react-redux';

import IconHeroCar from '@spothero/icons/hero-car';
import {
    Box,
    Button,
    Drawer,
    DrawerBody,
    Flex,
    Heading,
    Icon,
    Portal,
    Text,
    useBreakpointValue,
} from '@spothero/ui';

import Multiselect from 'common/multiselect';
import Input from 'common/input/Input';
import {email as emailRegex} from 'utils/patterns';
import {useEffectOnMount} from 'hooks/use-effect-on-mount/useEffectOnMount';
import useCancellableDispatch from 'hooks/api/use-cancellable-dispatch';
import useAuthAPISetup from 'common/auth/useAuthAPISetup';
import {
    getFilterOptionsData,
    getSearchedFacilitiesData,
    updateAppliedFilters,
} from 'pages/facilities/store/facilities-actions';
import {
    selectAppliedFilters,
    selectFilterOptions,
} from 'pages/facilities/store/facilities-selectors';

const labelStyles = {
    fontSize: 'xs',
    color: 'text.secondary.light',
    fontWeight: 'semibold',
};

const FiltersView = ({areFiltersOpen, containerRef, onFiltersToggle}) => {
    const emailInputRef = useRef();
    const dispatch = useDispatch();
    const {authAPIReady} = useAuthAPISetup();
    const {cities, status} = useSelector(selectFilterOptions);
    const {is_staff: isStaff} = useSelector((state) => state.user.data);
    const appliedFilters = useSelector(selectAppliedFilters);
    const {meta} = useSelector((state) => state.facilities);
    const {
        cities: appliedCities,
        status: appliedStatus,
        operatorEmail: operatorEmailFilter,
        searchText,
    } = appliedFilters;
    const [hasError, setHasError] = useState(false);
    const [operatorEmail, setOperatorEmail] = useState(
        operatorEmailFilter ? operatorEmailFilter : ''
    );
    const {cancellableDispatch, abortCurrent} = useCancellableDispatch();
    const isMobile = useBreakpointValue({base: true, tablet: false});
    const [selectedCities, setSelectedCities] = useState([]);
    const [selectedStatus, setSelectedStatus] = useState([]);
    const facilityStatusSortFn = (a, b) => Number(a.id) - Number(b.id);

    const loadData = useCallback(async () => {
        try {
            if (!authAPIReady) {
                return;
            }

            await cancellableDispatch(getFilterOptionsData);
        } catch (fetchError) {
            console.error('getFilterOptionsData error', fetchError);
        }
    }, [authAPIReady, cancellableDispatch]);

    const handleOperatorEmailChange = (event) => {
        const trimmedValue = event.target.value.trim();
        const regex = new RegExp(emailRegex);

        if (!regex.test(trimmedValue) && trimmedValue.length > 0) {
            setHasError(true);

            return;
        }

        setHasError(false);
        setOperatorEmail(trimmedValue);
    };

    const handleApplyFilters = () => {
        const filters = {
            cities: selectedCities ? selectedCities : [],
            status: selectedStatus ? selectedStatus : [],
            operatorEmail,
        };

        dispatch(updateAppliedFilters(filters));
        dispatch(
            getSearchedFacilitiesData({
                limit: meta.limit,
                searchText,
                filters,
            })
        );

        onFiltersToggle();
    };

    const handleOnClose = () => {
        setHasError(false);
        onFiltersToggle();
        setSelectedCities(appliedCities);
        setSelectedStatus(appliedStatus);
        setOperatorEmail(operatorEmailFilter);
    };

    const handleClearAllCities = () => {
        setSelectedCities([]);
    };

    const handleClearAllStatus = () => {
        setSelectedStatus([]);
    };

    const handleClearAllFilters = () => {
        setSelectedCities([]);
        setSelectedStatus([]);
        setOperatorEmail('');
        // Set the input value to an empty string, since we are not using the state to control the input value
        emailInputRef.current.value = '';
    };

    useEffectOnMount(() => {
        loadData();

        return () => {
            abortCurrent();
        };
    });

    useEffect(() => {
        setSelectedCities(appliedCities);
        setSelectedStatus(appliedStatus);
        setOperatorEmail(operatorEmailFilter);
    }, [appliedCities, appliedStatus, operatorEmailFilter]);

    return (
        <Portal containerRef={containerRef}>
            <Drawer
                isOpen={areFiltersOpen}
                onClose={handleOnClose}
                blockScrollOnMount={false}
                placement="left"
                size={isMobile ? 'full' : 'sidebar'}
                header="Filter Facilities"
            >
                <DrawerBody paddingX={3} paddingY={4} height="100%">
                    <Flex
                        marginBottom={4}
                        justifyContent="space-between"
                        alignItems="baseline"
                    >
                        <Heading as="h4" fontSize="sm">
                            Filter by:
                        </Heading>
                        <Button
                            onClick={handleClearAllFilters}
                            variant="tertiary"
                            fontSize="xs"
                        >
                            Clear all
                        </Button>
                    </Flex>
                    <Multiselect
                        options={cities}
                        value={selectedCities}
                        label="City"
                        subLabel="Select or type in a city"
                        onChange={setSelectedCities}
                        onClearAll={handleClearAllCities}
                        placeholder="Select Cities"
                        searchPlaceholder="Search for a City"
                        noResultsMessage="No Cities found"
                    />
                    <Multiselect
                        options={status}
                        value={selectedStatus}
                        label="Status"
                        onChange={setSelectedStatus}
                        onClearAll={handleClearAllStatus}
                        placeholder="Select Status"
                        searchPlaceholder="Search for a Status"
                        noResultsMessage="No Statuses found"
                        sortByFn={facilityStatusSortFn}
                    />
                    {isStaff && (
                        <Box>
                            <Text {...labelStyles}>Operator Email</Text>
                            <Flex
                                alignItems="center"
                                gap={0.5}
                                marginBottom={1}
                                height={4}
                            >
                                <Icon
                                    as={IconHeroCar}
                                    fill="text.secondary.light"
                                    boxSize={4}
                                    padding={0.5}
                                />
                                <Text
                                    {...labelStyles}
                                    fontSize="xs"
                                    fontWeight="normal"
                                    lineHeight={1}
                                >
                                    SpotHero Admin Only
                                </Text>
                            </Flex>
                            <Input
                                ref={emailInputRef}
                                id="operatorEmail"
                                placeholder="Enter an email to filter by"
                                errorMessage="Invalid email"
                                defaultValue={operatorEmail}
                                onChange={handleOperatorEmailChange}
                                width="full"
                                isInvalid={hasError}
                            />
                        </Box>
                    )}
                    <Box marginTop={10}>
                        <Button
                            onClick={handleApplyFilters}
                            isDisabled={hasError}
                            width="100%"
                        >
                            Apply
                        </Button>
                        <Button
                            onClick={handleOnClose}
                            marginTop={4}
                            width="100%"
                            variant="secondary"
                        >
                            Cancel
                        </Button>
                    </Box>
                </DrawerBody>
            </Drawer>
        </Portal>
    );
};

FiltersView.propTypes = {
    areFiltersOpen: PropTypes.bool.isRequired,
    containerRef: PropTypes.object,
    onFiltersToggle: PropTypes.func.isRequired,
    handleChipClick: PropTypes.func.isRequired,
    numFiltersApplied: PropTypes.number.isRequired,
    handleClearAllFilters: PropTypes.func.isRequired,
};

export default FiltersView;
